import geojsonApartmentsURL from "@/assets/features-apartments.json" with { type: "file" };
import geojsonBuildingsURL from "@/assets/features-buildings.json" with { type: "file" };
import type { Filters } from "@/filters";
import { divergentColorScale } from "@/lib/colorScale";
import type { Apartment, Building } from "@/types";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { renderToString } from "react-dom/server";
import { ApartmentPopup } from "./ApartmentPopup";
import { BuildingPopup } from "./BuildingPopup";
import { setInitialized } from "@/stores/map";

const MAPTILER_API_KEY = `mqsTARDxVutyTmWwaeu7`;
const MAP_CENTER: [number, number] = [4.868975, 52.36487];
const MAP_START_ZOOM = 13;
const MAP_MIN_PRICE = 5000;
const MAP_MAX_PRICE = 13000;

export class DataMap {
  buildingPopup: maplibregl.Popup | undefined;

  #data: {
    apartments: GeoJSON.FeatureCollection<GeoJSON.Point, Apartment>;
    buildings: GeoJSON.FeatureCollection<GeoJSON.Polygon, Building>;
  } = {
    apartments: {
      type: "FeatureCollection",
      features: [],
    },
    buildings: {
      type: "FeatureCollection",
      features: [],
    },
  };

  map: maplibregl.Map | undefined;

  makeScale<T>(min: number, max: number, scale: T[] | readonly T[]) {
    const steps = scale.length;
    const stepSize = (max - min) / steps;
    let current = min;
    const out: (number | T)[] = [];
    for (const color of scale) {
      out.push(current);
      out.push(color);
      current += stepSize;
    }
    return out;
  }

  async initSources(map: maplibregl.Map) {
    const geojsonApartmentsPromise = fetch(geojsonApartmentsURL as string)
      .then((res) => res.json())
      .then((geojson) => {
        this.#data.apartments = geojson;
      });
    const geojsonBuildingsPromise = fetch(geojsonBuildingsURL as string)
      .then((res) => res.json())
      .then((geojson) => {
        this.#data.buildings = geojson;
      });
    await Promise.all([geojsonApartmentsPromise, geojsonBuildingsPromise]);

    map.addSource("apartments", {
      type: "geojson",
      data: this.#data.apartments,
    });
    map.addSource("buildings", {
      type: "geojson",
      data: this.#data.buildings,
    });
  }

  sellingPriceColorScale = this.makeScale(
    MAP_MIN_PRICE,
    MAP_MAX_PRICE,
    divergentColorScale,
  );

  findCenter(
    coordinates: [number, number][] | [number, number, number][],
  ): [number, number] {
    let sumLon = 0;
    let sumLat = 0;
    const totalCoords = coordinates.length;

    coordinates.forEach((coord) => {
      sumLon += coord[0]; // Sum of longitudes
      sumLat += coord[1]; // Sum of latitudes
    });

    const centerLon = sumLon / totalCoords;
    const centerLat = sumLat / totalCoords;

    return [centerLon, centerLat];
  }

  addBuildings(map: maplibregl.Map, options?: { "3d": boolean }) {
    map.addLayer({
      id: "buildings",
      type: "fill",
      source: "buildings",
      paint: {
        "fill-color": [
          "interpolate",
          ["linear"],
          ["get", "selling_price_per_meter"],
          ...this.sellingPriceColorScale,
        ],
        "fill-opacity": [
          "interpolate",
          ["linear"],
          ["get", "year_built"],
          1890,
          0.5,
          2025,
          1,
        ],
      },
    });
    if (options?.["3d"]) {
      map.addLayer({
        id: "3d-buildings",
        source: "buildings",
        type: "fill-extrusion",
        minzoom: 13,
        paint: {
          "fill-extrusion-color": [
            "interpolate",
            ["linear"],
            ["get", "selling_price_per_meter"],
            ...this.sellingPriceColorScale,
          ],
          // prettier-ignore
          "fill-extrusion-height": [
            "interpolate",
            ["linear"],
            ["zoom"],
            3, 1,
            20, ["*", ["get", "selling_price_per_meter"], 0.01],
          ],
          "fill-extrusion-base": 0,
        },
      });
    }

    map.on("mouseenter", "buildings", () => {
      map.getCanvas().style.cursor = "pointer";
    });
    map.on("mouseleave", "buildings", () => {
      map.getCanvas().style.cursor = "";
    });

    map.on("click", "buildings", (e) => {
      // Change the cursor style as a UI indicator.
      map.getCanvas().style.cursor = "pointer";

      const geometry = e.features?.[0].geometry;
      if (geometry?.type !== "Polygon") {
        return;
      }
      const coordinates = this.findCenter(
        geometry?.coordinates?.[0] as [number, number][],
      );
      const properties = e.features?.[0].properties;
      if (properties === undefined) {
        return;
      }

      // Ensure that if the map is zoomed out such that multiple
      // copies of the feature are visible, the popup appears
      // over the copy being pointed to.
      while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
      }

      const table = renderToString(
        <BuildingPopup
          // @ts-expect-error
          building={properties}
        />,
      );
      const popup = new maplibregl.Popup()
        .setLngLat(coordinates)
        .setHTML(table)
        .addTo(map);
      this.buildingPopup = popup;
    });
  }

  addPoints(map: maplibregl.Map) {
    map.addLayer({
      id: "apartments",
      type: "circle",
      source: "apartments",
      minzoom: 15,
      paint: {
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 15, 4, 20, 10],
        "circle-stroke-color": "white",
        "circle-stroke-width": 1,
        "circle-color": [
          "interpolate",
          ["linear"],
          ["get", "selling_price_per_meter"],
          ...this.sellingPriceColorScale,
        ],
      },
    });

    map.on("mouseenter", "apartments", () => {
      map.getCanvas().style.cursor = "pointer";
    });
    map.on("mouseleave", "apartments", () => {
      map.getCanvas().style.cursor = "";
    });

    map.on("click", "apartments", (e) => {
      // Change the cursor style as a UI indicator.
      map.getCanvas().style.cursor = "pointer";

      if (e.features?.[0].geometry?.type !== "Point") {
        return;
      }
      const coordinates = e.features?.[0].geometry?.coordinates.slice();
      const properties = e.features?.[0].properties;
      if (properties === undefined) {
        return;
      }

      // Ensure that if the map is zoomed out such that multiple
      // copies of the feature are visible, the popup appears
      // over the copy being pointed to.
      while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
      }

      const table = renderToString(
        <ApartmentPopup
          // @ts-expect-error
          apartment={properties}
        />,
      );
      new maplibregl.Popup()
        .setLngLat(coordinates as [number, number])
        .setHTML(table)
        .addTo(map);
      this.buildingPopup?.remove();
    });
  }

  init(ref: HTMLElement) {
    const map = new maplibregl.Map({
      container: ref,
      style: `https://api.maptiler.com/maps/dataviz/style.json?key=${MAPTILER_API_KEY}`,
      center: MAP_CENTER,
      zoom: MAP_START_ZOOM,
    });
    map.on("load", async () => {
      await this.initSources(map);
      this.addBuildings(map);
      this.addPoints(map);
      setInitialized(true);
    });
    this.map = map;
  }

  /**
   * Mapbox GL JS does not support filtering on multiple sources.
   * This function filters the buildings based on the apartments that are
   * currently filtered and updates the selling price per meter for each building
   * to reflect the filtered apartments.
   */
  #filterBuildings(filters: Filters) {
    const buildingAggr: Record<string, { count: number; sum: number }> = {};
    for (const apartment of this.#data.apartments.features) {
      if (filters.filterApartment(apartment.properties)) {
        if (buildingAggr[apartment.properties.building_id] === undefined) {
          buildingAggr[apartment.properties.building_id] = { count: 0, sum: 0 };
        }
        buildingAggr[apartment.properties.building_id].count += 1;
        buildingAggr[apartment.properties.building_id].sum +=
          apartment.properties.selling_price_per_meter;
      }
    }

    const buildingFeatures = this.#data.buildings.features
      .filter((f) => buildingAggr[f.properties.id] !== undefined)
      .map((feature) => {
        const building = buildingAggr[feature.properties.id];
        return {
          ...feature,
          properties: {
            ...feature.properties,
            selling_price_per_meter: building?.sum / (building?.count ?? 1),
          },
        };
      });

    (this.map?.getSource("buildings") as maplibregl.GeoJSONSource).setData({
      type: "FeatureCollection",
      features: buildingFeatures,
    });
  }

  filter(filters: Filters) {
    this.map?.setFilter("apartments", filters.specification);
    this.#filterBuildings(filters);
  }
}
